import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import MessageBubble from './MessageBubble';
import CategorySection from './CategorySection';
import TypingIndicator from './TypingIndicator';
import '../../assets/styles/ChatPanel.css';
import {
    downloadChatMessagesHistory,
    getChatMessages,
    getGeminiAgentAnswer,
    getOpenaiAgentAnswer,
    getPresetQuestions
} from "../../axios-client";
import i18n from "../../config/i18n";
import { RTL_LANGUAGES } from "../../constants/constants";
import PropTypes from 'prop-types';
import {triggerFileDownload} from "../../utlis/generalUtils";
import { Tooltip, IconButton } from "@mui/material";
import {useSoftUIController} from "../../context";

const PAGE_SIZE = 10;

const ChatPanel = ({ digitizationId, onClose }) => {
    const [controller] = useSoftUIController();
    const {uuid} = controller;
    const [messages, setMessages] = useState([]);
    const [page, setPage] = useState(1);
    const [allMessagesLoaded, setAllMessagesLoaded] = useState(false);
    const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
    const [input, setInput] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [showQuestions, setShowQuestions] = useState(true);
    const [questionsByCategory, setQuestionsByCategory] = useState([]);
    const [loadingQuestions, setLoadingQuestions] = useState(true);
    const [isMinimized, setIsMinimized] = useState(false);
    const messagesContainerRef = useRef(null);
    const messagesEndRef = useRef(null);
    const { t } = useTranslation(['chatbot']);
    const currentLang = i18n.language;
    const isRTL = RTL_LANGUAGES.includes(currentLang);

    useEffect(() => {
        setLoadingQuestions(true);
        getPresetQuestions(currentLang)
            .then((response) => {
                setQuestionsByCategory(response.data);
            })
            .catch((error) => {
                console.error("Failed to fetch preset questions:", error);
            })
            .finally(() => {
                setLoadingQuestions(false);
            });
    }, [currentLang]);

    const fetchMessages = async (pageNumber) => {
        try {
            const response = await getChatMessages(digitizationId, pageNumber, PAGE_SIZE);
            const data = response.data;

            const adaptedMessages = data.messages.map(msg => ({
                id: msg.id,
                text: msg.content,
                timestamp: new Date(msg.created_at).getTime(),
                type: msg.sender,
            }));

            const hasMore = (pageNumber * PAGE_SIZE) < data.total;

            return { messages: adaptedMessages, hasMore };
        } catch (error) {
            console.error("Error fetching messages:", error);
            return { messages: [], hasMore: false };
        }
    };

    useEffect(() => {
        if (!digitizationId) return;
        const loadInitialMessages = async () => {
            setLoadingOlderMessages(true);
            const { messages: initialMessages, hasMore } = await fetchMessages(1);
            setMessages(initialMessages);
            setPage(1);
            setAllMessagesLoaded(!hasMore);
            setLoadingOlderMessages(false);
            scrollToBottom();
        };
        loadInitialMessages();
    }, [digitizationId]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        if (!loadingOlderMessages && !isTyping) {
            scrollToBottom();
        }
    }, [messages, isTyping]);

    useEffect(() => {
        const container = messagesContainerRef.current;
        if (!container) return;

        const handleScroll = async () => {
            if (
                container.scrollTop === 0 &&
                !allMessagesLoaded &&
                !loadingOlderMessages
            ) {
                setLoadingOlderMessages(true);
                const nextPage = page + 1;
                const prevScrollHeight = container.scrollHeight;

                const { messages: olderMessages, hasMore } = await fetchMessages(nextPage);

                setMessages(prevMessages => [...olderMessages, ...prevMessages]);
                setPage(nextPage);
                setAllMessagesLoaded(!hasMore);

                requestAnimationFrame(() => {
                    const newScrollHeight = container.scrollHeight;
                    container.scrollTop = newScrollHeight - prevScrollHeight;
                    setLoadingOlderMessages(false);
                });
            }
        };

        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
    }, [page, allMessagesLoaded, loadingOlderMessages]);

    const simulateAIResponse = async (question) => {
        setIsTyping(true);
        try {
            const res = await getGeminiAgentAnswer(digitizationId, "yai4edu", question, uuid);
            const answer = res.data.answer;
            setMessages(prev => [
                ...prev,
                {
                    timestamp: Date.now(),
                    text: answer,
                    type: 'agent'
                }
            ]);
        } catch (geminiError) {
            try {
                const res = await getOpenaiAgentAnswer(digitizationId, "yai4edu", question, uuid);
                const answer = res.data.answer;
                setMessages(prev => [
                    ...prev,
                    {
                        timestamp: Date.now(),
                        text: answer,
                        type: 'agent'
                    }
                ]);
            } catch (openaiError) {
                setMessages(prev => [
                    ...prev,
                    {
                        timestamp: Date.now(),
                        text: t('chatbot.errorMessage'),
                        type: 'agent'
                    }
                ]);
            }
        } finally {
            setIsTyping(false);
        }
    };

    const handleQuestionClick = async (question) => {
        const userMessage = {
            type: 'user',
            text: question,
            timestamp: Date.now()
        };
        setMessages(prev => [...prev, userMessage]);
        await simulateAIResponse(question);
    };

    const handleInputSubmit = async () => {
        if (!input.trim()) return;
        const question = input.trim();
        setInput('');
        await handleQuestionClick(question);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleInputSubmit();
        }
    };

    const downloadHistory = () => {
        downloadChatMessagesHistory(digitizationId, { responseType: 'blob' })
            .then((response) => {
                triggerFileDownload(new Blob([response.data]), `chat_digitization_${digitizationId}.pdf`);
            })
            .catch((error) => {
                console.error("Failed to download chat history:", error);
            })
            .finally(() => {
                setLoadingQuestions(false);
            });
    };

    return (
        <div
            className={`chat-panel ${isMinimized ? 'minimized' : ''}`}
            dir={isRTL ? 'rtl' : 'ltr'}
            style={{ direction: isRTL ? 'rtl' : 'ltr' }}
        >
            <div className="chat-header">
                <div className="header-content">
                    <div className="assistant-info">
                        <div className="assistant-avatar">
                            <svg className="sparkles-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                 strokeWidth="2">
                                <path
                                    d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/>
                                <path d="M5 3v4"/>
                                <path d="M19 17v4"/>
                                <path d="M3 5h4"/>
                                <path d="M17 19h4"/>
                            </svg>
                        </div>
                        <div className="assistant-text">
                            <h3>{t('chatbot.assistantTitle')}</h3>
                            <p>{t('chatbot.assistantSubtitle')}</p>
                        </div>
                    </div>
                    <div>
                        <Tooltip title={t('chatbot.exportChat')} arrow>
                            <IconButton
                                className="close-button"
                                onClick={downloadHistory}
                                size="small"
                            >
                                <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                    <polyline points="7 10 12 15 17 10"/>
                                    <line x1="12" y1="15" x2="12" y2="3"/>
                                </svg>
                            </IconButton>
                        </Tooltip>

                        <Tooltip title={isMinimized ? t('chatbot.expandChat') : t('chatbot.minimizeChat')} arrow>
                            <IconButton
                                className="close-button"
                                onClick={() => setIsMinimized(!isMinimized)}
                                size="small"
                            >
                                {isMinimized ? (
                                    <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                                        <polyline points="6 15 12 9 18 15"/>
                                    </svg>
                                ) : (
                                    <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                                        <polyline points="18 9 12 15 6 9"/>
                                    </svg>
                                )}
                            </IconButton>
                        </Tooltip>

                        <Tooltip title={t('chatbot.closeChat')} arrow>
                            <IconButton
                                className="close-button"
                                onClick={onClose}
                                size="small"
                            >
                                <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"/>
                                    <line x1="6" y1="6" x2="18" y2="18"/>
                                </svg>
                            </IconButton>
                        </Tooltip>
                    </div>
                </div>
            </div>

            {!isMinimized && (
                <>

                <div className="messages-area" ref={messagesContainerRef}>
                        <div className="messages-content" dir={"ltr"}>
                            {loadingOlderMessages && (
                                <div className="loader" style={{textAlign: 'center', padding: '10px'}}>
                                    <span className="spinner"/>
                                </div>
                            )}

                            {messages.length === 0 && !loadingOlderMessages && (
                                <div className="welcome-message">
                                    <div className="welcome-icon">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path
                                                d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/>
                                        </svg>
                                    </div>
                                    <p dir={isRTL ? 'rtl' : 'ltr'}>👋 {t('chatbot.welcome')}</p>
                                </div>
                            )}

                            {messages.map((message, idx) => (
                                <MessageBubble
                                    key={message.id || idx}
                                    type={message.type}
                                    text={message.text}
                                    timestamp={message.timestamp}
                                    isRTL={isRTL}
                                />
                            ))}

                            {isTyping && <TypingIndicator/>}
                            <div ref={messagesEndRef}/>
                        </div>
                    </div>

                    <div className="questions-section">
                        <div
                            className="questions-header"
                            onClick={() => setShowQuestions(!showQuestions)}
                            style={{cursor: 'pointer'}}
                        >
                            <h4>{showQuestions ? t('chatbot.quickQuestionsExpanded') : t('chatbot.quickQuestionsCollapsed')}</h4>
                        </div>
                        {showQuestions && !loadingQuestions && (
                            <div className="questions-content">
                                {questionsByCategory.map((category) => (
                                    <CategorySection
                                        key={category.id}
                                        data={{
                                            title: category.category,
                                            questions: category.questions.map(q => q.question),
                                            icon: category.icon
                                        }}
                                        onQuestionClick={handleQuestionClick}
                                        isRTL={isRTL}
                                    />
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="chat-input-area">
                        <div className="input-container">
                            <input
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={handleKeyPress}
                                placeholder={t('chatbot.inputPlaceholder')}
                                className="message-input"
                                style={{textAlign: isRTL ? 'right' : 'left'}}
                                disabled={isTyping}
                            />
                            <button
                                onClick={handleInputSubmit}
                                disabled={!input.trim() || isTyping}
                                className="send-button"
                            >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="22" y1="2" x2="11" y2="13"/>
                                    <polygon points="22,2 15,22 11,13 2,9 22,2"/>
                                </svg>
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

ChatPanel.propTypes = {
    digitizationId: PropTypes.number,
    onClose: PropTypes.func,
};

export default ChatPanel;
